import { Auth } from 'aws-amplify';

import { LENS_MODEL_OPTIONS } from '../config/lensConfig';

const SESSION_HEADER = 'X-Amzn-Bedrock-AgentCore-Runtime-Session-Id';
const ALLOWED_MODELS = new Set(LENS_MODEL_OPTIONS.map(({ key }) => key));

function tokenValue(token) {
  return token?.getJwtToken?.() || null;
}

function sessionId() {
  const random = Math.random().toString(36).slice(2);
  return `mmir-lens-${Date.now()}-${random}`.padEnd(33, '0');
}

function imagePayload(imageDataUrl) {
  const match = /^data:(image\/(?:jpeg|png|webp));base64,(.+)$/i.exec(imageDataUrl || '');
  if (!match) {
    throw new Error('The selected image could not be prepared.');
  }
  return { mimeType: match[1].toLowerCase(), base64: match[2] };
}

function appErrorStatus(code) {
  if (code === 'BAD_REQUEST') return 400;
  if (code === 'FORBIDDEN') return 403;
  return 502;
}

async function parseResponse(response) {
  let body = null;
  try {
    body = await response.json();
  } catch (error) {
    body = null;
  }
  const json = body && typeof body === 'object' ? body : {};
  if (!response.ok) {
    return {
      statusCode: response.status,
      message: response.status === 401
        ? 'Your session has expired. Please sign in again.'
        : json.error?.message || json.message || response.statusText || 'Request failed.',
    };
  }
  if (json.error) {
    return {
      ...json,
      statusCode: appErrorStatus(json.error.code),
      message: json.error.message || 'The service could not complete this request.',
    };
  }
  return { statusCode: response.status, ...json };
}

/**
 * Lens owns this client so changes to its request/auth contract cannot alter
 * the existing /visualqa or /unifiedapp flows.
 */
export default class LensClient {
  constructor(apiHost, auth = Auth, fetchImpl = (...args) => window.fetch(...args)) {
    this.apiHost = apiHost;
    this.auth = auth;
    this.fetchImpl = fetchImpl;
    this.sessionId = sessionId();
  }

  resetSession = () => {
    this.sessionId = sessionId();
  };

  getAccessToken = async (forceRefresh = false) => {
    if (forceRefresh) {
      await this.auth.currentAuthenticatedUser({ bypassCache: true });
    }
    return tokenValue((await this.auth.currentSession())?.getAccessToken?.());
  };

  send = async (token, query, image, model) => {
    const response = await this.fetchImpl(this.apiHost, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
        [SESSION_HEADER]: this.sessionId,
      },
      body: JSON.stringify({
        query,
        image,
        options: {
          model,
          retrievalDepth: 10,
          vtagMode: 'full',
        },
      }),
    });
    return parseResponse(response);
  };

  ask = async ({ query, imageDataUrl, model = 'sonnet' }) => {
    if (!ALLOWED_MODELS.has(model)) {
      return { statusCode: 400, message: 'Choose a supported model.' };
    }
    let image = null;
    try {
      image = imagePayload(imageDataUrl);
    } catch (error) {
      return { statusCode: 400, message: error.message };
    }

    let lastUnauthorized = null;
    for (const forceRefresh of [false, true]) {
      let token = null;
      try {
        token = await this.getAccessToken(forceRefresh);
      } catch (error) {
        return { statusCode: 401, message: 'Authentication is required.' };
      }
      if (!token) {
        return { statusCode: 401, message: 'Authentication is required.' };
      }
      const result = await this.send(token, query, image, model);
      if (result.statusCode !== 401) return result;
      lastUnauthorized = result;
    }
    return lastUnauthorized || { statusCode: 401, message: 'Authentication is required.' };
  };

}
